/**
 * Close Readiness — Wave D-1.
 *
 * "Continuous close" mindset: never wait for month-end to find out the books
 * aren't ready. This selector composes every existing health signal into a
 * single 0-100 score with a per-signal breakdown. The Close button just
 * freezes a state that's already green.
 *
 * Pure derivation — no writes, no provider coupling. Plug into any dashboard
 * by passing in GL state + AP/bank txns.
 */
import type { JournalEntry, GLAccount, AccountBalance } from "./glTypes";
import type { Bill } from "./ap/apTypes";
import type { BankTxn } from "./bankFeed/bankFeedTypes";
import { getARAgingSummary } from "./ar/arAging";
import { getWipAgingSummary } from "./reports/wipAging";
import { getAbnormalBalances } from "./reports/abnormalBalances";
import { detectAnomalies } from "./journalAnomalies";

export type SignalStatus = "green" | "yellow" | "red";

export interface ReadinessSignal {
  key:
    | "bank_reconciliation"
    | "ar_overdue"
    | "ap_overdue"
    | "abnormal_balances"
    | "stale_wip"
    | "unapplied_credits"
    | "period_lag"
    | "journal_anomalies";
  label: string;
  status: SignalStatus;
  /** 0-100 contribution this signal currently scores (out of `weight`). */
  score: number;
  /** Max points this signal can contribute. Sum of weights = 100. */
  weight: number;
  /** Short headline metric, e.g. "3 of 47 unmatched · 6.4%". */
  detail: string;
  /** What the operator should do to clear the flag. */
  remedy: string;
  /** Deep-link target route for one-click triage. */
  route: string;
}

export interface CloseReadinessReport {
  /** Composite 0-100. 90+ = green, 75-89 = yellow, <75 = red. */
  score: number;
  status: SignalStatus;
  signals: ReadinessSignal[];
  /** ISO yyyy-mm of the period this report describes. */
  period: string;
  computedAt: string;
}

// Sum = 100. Anomalies (D-2) carry 10 points; rebalanced from abnormal (15→10)
// and period_lag (10→5) so the most material signals (bank, AR, AP) stay weighted.
const WEIGHTS = {
  bank_reconciliation: 25,
  ar_overdue: 15,
  ap_overdue: 15,
  abnormal_balances: 10,
  stale_wip: 15,
  unapplied_credits: 5,
  period_lag: 5,
  journal_anomalies: 10,
} as const;

/** Linear score: 1.0 at `green` value, 0.0 at `red` value, clamped. */
function band(value: number, green: number, red: number): number {
  if (red === green) return value <= green ? 1 : 0;
  const t = (value - green) / (red - green);
  return Math.max(0, Math.min(1, 1 - t));
}

function statusFor(pct: number): SignalStatus {
  if (pct >= 0.9) return "green";
  if (pct >= 0.6) return "yellow";
  return "red";
}

export interface CloseReadinessInput {
  journalEntries: JournalEntry[];
  accounts: GLAccount[];
  trialBalance: AccountBalance[];
  bills: Bill[];
  bankTxns: BankTxn[];
  asOf?: string;
}

export function getCloseReadiness(input: CloseReadinessInput): CloseReadinessReport {
  const asOf = input.asOf ?? new Date().toISOString().slice(0, 10);
  const period = asOf.slice(0, 7);
  const today = new Date(asOf);

  // ─── 1. Bank reconciliation coverage ────────────────────────────────────
  const liveTxns = input.bankTxns.filter(t => t.status !== "ignored");
  const matched = liveTxns.filter(t => t.status === "matched").length;
  const totalTxns = liveTxns.length;
  const matchPct = totalTxns === 0 ? 1 : matched / totalTxns;
  const unmatched = totalTxns - matched;
  const bankScorePct = totalTxns === 0 ? 1 : band(1 - matchPct, 0.02, 0.15); // <=2% unmatched = green, >=15% = red
  const bankSignal: ReadinessSignal = {
    key: "bank_reconciliation",
    label: "Bank reconciliation",
    status: statusFor(bankScorePct),
    score: bankScorePct * WEIGHTS.bank_reconciliation,
    weight: WEIGHTS.bank_reconciliation,
    detail: totalTxns === 0
      ? "No bank activity imported."
      : `${matched} of ${totalTxns} matched · ${unmatched} open`,
    remedy: unmatched === 0
      ? "Bank rec is clean."
      : `Match the ${unmatched} open transaction${unmatched === 1 ? "" : "s"} on the bank rec page.`,
    route: "/accounting/bank-rec",
  };

  // ─── 2. AR overdue ──────────────────────────────────────────────────────
  const ar = getARAgingSummary(input.journalEntries, asOf);
  const arOverduePct = ar.totalOpen <= 0.005 ? 0 : ar.overdueAmount / ar.totalOpen;
  const arScorePct = ar.totalOpen <= 0.005 ? 1 : band(arOverduePct, 0.05, 0.35);
  const arSignal: ReadinessSignal = {
    key: "ar_overdue",
    label: "AR overdue (>30d)",
    status: statusFor(arScorePct),
    score: arScorePct * WEIGHTS.ar_overdue,
    weight: WEIGHTS.ar_overdue,
    detail: ar.totalOpen <= 0.005
      ? "No open AR."
      : `$${ar.overdueAmount.toFixed(0)} of $${ar.totalOpen.toFixed(0)} (${(arOverduePct * 100).toFixed(0)}%) past 30d`,
    remedy: ar.overdueAmount <= 0
      ? "AR is current."
      : `Run dunning on the ${ar.bucketCustomerCount["31-60"] + ar.bucketCustomerCount["61-90"] + ar.bucketCustomerCount["90+"]} overdue customers.`,
    route: "/accounting/ar",
  };

  // ─── 3. AP overdue ──────────────────────────────────────────────────────
  let apOpen = 0;
  let apOverdue = 0;
  let overdueBills = 0;
  for (const b of input.bills) {
    if (b.status === "void" || b.status === "paid") continue;
    const open = b.total - b.amountPaid;
    if (open <= 0.005) continue;
    apOpen += open;
    const due = new Date(b.dueDate);
    if (due < today) { apOverdue += open; overdueBills += 1; }
  }
  const apOverduePct = apOpen <= 0.005 ? 0 : apOverdue / apOpen;
  const apScorePct = apOpen <= 0.005 ? 1 : band(apOverduePct, 0.05, 0.4);
  const apSignal: ReadinessSignal = {
    key: "ap_overdue",
    label: "AP overdue",
    status: statusFor(apScorePct),
    score: apScorePct * WEIGHTS.ap_overdue,
    weight: WEIGHTS.ap_overdue,
    detail: apOpen <= 0.005
      ? "No open AP."
      : `${overdueBills} bill${overdueBills === 1 ? "" : "s"} past due · $${apOverdue.toFixed(0)} of $${apOpen.toFixed(0)}`,
    remedy: overdueBills === 0
      ? "All bills current."
      : `Pay or renegotiate ${overdueBills} overdue bill${overdueBills === 1 ? "" : "s"}.`,
    route: "/accounting/ap",
  };

  // ─── 4. Abnormal balances ───────────────────────────────────────────────
  const abnormal = getAbnormalBalances(input.trialBalance, input.accounts);
  const critical = abnormal.filter(a => a.severity === "critical").length;
  const warn = abnormal.filter(a => a.severity === "warn").length;
  // Critical (cash overdrawn, AR credit, AP debit) is an instant red flag.
  const abnormalPct = critical > 0 ? 0 : warn === 0 ? 1 : band(warn, 0, 5);
  const abnormalSignal: ReadinessSignal = {
    key: "abnormal_balances",
    label: "Abnormal balances",
    status: statusFor(abnormalPct),
    score: abnormalPct * WEIGHTS.abnormal_balances,
    weight: WEIGHTS.abnormal_balances,
    detail: abnormal.length === 0
      ? "All accounts on normal side."
      : `${critical} critical · ${warn} warn · ${abnormal.length - critical - warn} info`,
    remedy: abnormal.length === 0
      ? "Nothing to review."
      : "Investigate flagged accounts on the Trial Balance.",
    route: "/accounting/gl/trial-balance",
  };

  // ─── 5. Stale WIP (>90d) ────────────────────────────────────────────────
  const wip = getWipAgingSummary(input.journalEntries, asOf);
  const wipStalePct = wip.totalOpenBalance <= 0.005 ? 0 : wip.staleBalance / wip.totalOpenBalance;
  const wipScorePct = wip.totalOpenBalance <= 0.005 ? 1 : band(wipStalePct, 0.1, 0.5);
  const wipSignal: ReadinessSignal = {
    key: "stale_wip",
    label: "Stale WIP (>90d)",
    status: statusFor(wipScorePct),
    score: wipScorePct * WEIGHTS.stale_wip,
    weight: WEIGHTS.stale_wip,
    detail: wip.totalOpenBalance <= 0.005
      ? "No open WIP."
      : `$${wip.staleBalance.toFixed(0)} stale of $${wip.totalOpenBalance.toFixed(0)} · oldest ${wip.oldestAgeDays}d`,
    remedy: wip.staleBalance <= 0
      ? "WIP is fresh."
      : "Release WIP to COGS or write off — capitalized cost is aging.",
    route: "/accounting/reports/wip-aging",
  };

  // ─── 6. Unapplied customer credits ──────────────────────────────────────
  const unappliedPct = ar.totalUnappliedCredit <= 0.005
    ? 1
    : band(ar.unappliedCreditCustomerCount, 0, 5);
  const unappliedSignal: ReadinessSignal = {
    key: "unapplied_credits",
    label: "Unapplied customer credits",
    status: statusFor(unappliedPct),
    score: unappliedPct * WEIGHTS.unapplied_credits,
    weight: WEIGHTS.unapplied_credits,
    detail: ar.totalUnappliedCredit <= 0.005
      ? "No unapplied credits."
      : `$${ar.totalUnappliedCredit.toFixed(0)} across ${ar.unappliedCreditCustomerCount} customer${ar.unappliedCreditCustomerCount === 1 ? "" : "s"}`,
    remedy: ar.totalUnappliedCredit <= 0.005
      ? "Nothing to apply."
      : "Apply credits to open invoices or refund.",
    route: "/accounting/ar",
  };

  // ─── 7. Period lag (oldest open period vs. today) ───────────────────────
  // Find the oldest period with posted activity that hasn't been closed.
  // Closed periods are inferred from GL state via signature on the JE stream;
  // here we assume the caller has not closed the prior month if posted JEs
  // exist in it. We surface lag as months between oldest-open and today.
  const allPeriods = new Set<string>();
  for (const je of input.journalEntries) {
    if (je.status === "posted") allPeriods.add(je.period);
  }
  const sortedPeriods = [...allPeriods].sort();
  const oldestOpen = sortedPeriods[0] ?? period;
  const monthsLag = periodDiff(oldestOpen, period);
  // 0 months = same period (green), 3+ months lag = red.
  const lagPct = band(monthsLag, 0, 3);
  const lagSignal: ReadinessSignal = {
    key: "period_lag",
    label: "Period close cadence",
    status: statusFor(lagPct),
    score: lagPct * WEIGHTS.period_lag,
    weight: WEIGHTS.period_lag,
    detail: monthsLag === 0
      ? "Current period only."
      : `Oldest open period: ${oldestOpen} · ${monthsLag} month${monthsLag === 1 ? "" : "s"} behind`,
    remedy: monthsLag === 0
      ? "On cadence."
      : `Close ${oldestOpen} before working on the current period.`,
    route: "/accounting/periods",
  };

  // ─── 8. Journal anomalies (D-2 review queue) ────────────────────────────
  const review = detectAnomalies(input.journalEntries);
  const postedCount = input.journalEntries.filter(e => e.status === "posted").length;
  const lowConfidence = review.filter(r => r.confidence < 60).length;
  const flaggedRate = postedCount === 0 ? 0 : review.length / postedCount;
  // Green: no low-confidence items AND <2% of posted JEs flagged.
  // Red: ≥5 low-confidence items OR >20% flagged.
  const anomalyPct = lowConfidence === 0 && flaggedRate <= 0.02
    ? 1
    : Math.min(
        band(lowConfidence, 0, 5),
        band(flaggedRate, 0.02, 0.2),
      );
  const anomalySignal: ReadinessSignal = {
    key: "journal_anomalies",
    label: "Journal anomalies",
    status: statusFor(anomalyPct),
    score: anomalyPct * WEIGHTS.journal_anomalies,
    weight: WEIGHTS.journal_anomalies,
    detail: review.length === 0
      ? "No flagged entries."
      : `${review.length} flagged · ${lowConfidence} low-confidence`,
    remedy: review.length === 0
      ? "Nothing to review."
      : "Review flagged entries before sealing the period.",
    route: "/accounting/gl/review-queue",
  };

  const signals: ReadinessSignal[] = [
    bankSignal, arSignal, apSignal, abnormalSignal, wipSignal, unappliedSignal, lagSignal, anomalySignal,
  ];
  const score = signals.reduce((s, x) => s + x.score, 0);
  return {
    score: Math.round(score),
    status: statusFor(score / 100),
    signals,
    period,
    computedAt: new Date().toISOString(),
  };
}

function periodDiff(from: string, to: string): number {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
}
